import { yupResolver } from '@hookform/resolvers/yup';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import React, { useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { mixed, number, object, string } from 'yup';

import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Chip,
  CircularProgress,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  TextField,
  Typography,
} from '@mui/material';

import { apiEndPoints } from '../../../common/api.endPoints';
import { FetchService } from '../../../common/fetch.service';
import { OVERRIDE_REASONS } from '../overrideReasons';

// Sentinel used in the "Reassign To User" selector to represent bulk-unassign.
// Real user IDs are always positive, so a negative value is safe as a non-colliding sentinel.
const UNASSIGN_SENTINEL_ID = -1;
const UNASSIGN_OPTION: User = {
  id: UNASSIGN_SENTINEL_ID,
  firstName: '',
  lastName: '',
  displayName: 'Unassigned — clear assignment',
  email: '',
  active: true,
  paused: false,
  userOrganization: '',
};

// Form data interface
interface BulkReassignFormData {
  assignmentType: 'underwriter' | 'casemanager';
  fromUserId: number;
  // UNASSIGN_SENTINEL_ID (-1) represents bulk-unassign; positive = single target user.
  toUserId: number;
  overrideReason: string;
}

// Yup validation schema
const validationSchema = object().shape({
  assignmentType: mixed<'underwriter' | 'casemanager'>()
    .oneOf(
      ['underwriter', 'casemanager'],
      'Please select a valid assignment type',
    )
    .required('Assignment type is required'),
  fromUserId: number()
    .positive('Please select a user to reassign from')
    .required('From user is required'),
  toUserId: number()
    .required('Please select a user to reassign to, or choose Unassigned')
    .test(
      'not-zero',
      'Please select a user to reassign to, or choose Unassigned',
      v => v !== 0,
    ),
  overrideReason: string()
    .required('Override reason is required')
    .max(500, 'Override reason cannot exceed 500 characters'),
});

// Response interface
interface BulkReassignResponse {
  reassignedFrom: number;
  reassignedTo: number[];
  assignmentType: string;
  updatedCount: number;
  activity: any;
}

interface User {
  id: number;
  firstName: string;
  lastName: string;
  displayName: string;
  email: string;
  active: boolean;
  paused: boolean;
  userOrganization: string;
}

export function BulkUserReassignmentTab() {
  const [submitSuccess, setSubmitSuccess] =
    useState<BulkReassignResponse | null>(null);
  const [otherReasonText, setOtherReasonText] = useState('');
  const queryClient = useQueryClient();

  const {
    control,
    handleSubmit,
    watch,
    reset,
    formState: { errors, isValid },
  } = useForm<BulkReassignFormData>({
    resolver: yupResolver(validationSchema),
    mode: 'onChange',
    defaultValues: {
      assignmentType: 'underwriter',
      fromUserId: 0,
      toUserId: 0,
      overrideReason: '',
    },
  });

  const assignmentType = watch('assignmentType');
  const fromUserId = watch('fromUserId');
  const overrideReason = watch('overrideReason');
  const isOtherReasonSelected = overrideReason === 'Other';
  const otherReasonTooLong =
    isOtherReasonSelected && otherReasonText.length > 500;

  // Clear form when assignment type changes
  useEffect(() => {
    reset({
      assignmentType,
      fromUserId: 0,
      toUserId: 0,
      overrideReason: '',
    });
    setOtherReasonText('');
  }, [assignmentType, reset]);

  // Load users based on assignment type using React Query
  const {
    data: availableUsers = [],
    isLoading: loadingUsers,
    error: usersError,
  } = useQuery({
    queryKey: ['users', assignmentType],
    queryFn: async () => {
      const endpoint =
        assignmentType === 'underwriter'
          ? apiEndPoints.underwriters
          : apiEndPoints.caseManagers;

      // Include deactivated users so a manager can move cases off a deactivated
      // account via "Reassign From User" — filtered back out for "Reassign To User" below.
      const users: User[] = await FetchService.getInstance().get(endpoint, {
        params: { includeInactive: true },
      });
      return users || [];
    },
    staleTime: 5 * 60 * 1000,
    cacheTime: 10 * 60 * 1000,
  });

  // Bulk reassignment mutation
  const {
    mutate: performBulkReassignment,
    isLoading: loading,
    error: mutationError,
  } = useMutation({
    mutationFn: async (data: BulkReassignFormData) => {
      const isUnassign = data.toUserId === UNASSIGN_SENTINEL_ID;
      const response: BulkReassignResponse =
        await FetchService.getInstance().post(apiEndPoints.managerTabBulkUserReassignment, {
          body: {
            assignmentType: data.assignmentType,
            fromUserId: data.fromUserId,
            overrideReason: data.overrideReason,
            // Strip the sentinel — backend receives [] to signal bulk-unassign.
            toUserIds: isUnassign ? [] : [data.toUserId],
          },
        });
      return response;
    },
    onSuccess: (response, variables) => {
      setSubmitSuccess(response);
      reset({
        assignmentType: variables.assignmentType,
        fromUserId: 0,
        toUserId: 0,
        overrideReason: '',
      });
      setOtherReasonText('');
    },
    onError: (error: any) => {
      console.error('Bulk reassignment failed:', error);
    },
  });

  // Filter available target users: exclude the selected "from" user, paused users,
  // and deactivated accounts, with the "Unassigned" sentinel prepended so managers
  // can clear a deactivated user's cases.
  const availableTargetUsers = [
    UNASSIGN_OPTION,
    ...availableUsers.filter(
      user => user.id !== fromUserId && user.active && !user.paused,
    ),
  ];

  const onSubmit = (data: BulkReassignFormData) => {
    setSubmitSuccess(null);
    const effectiveReason =
      data.overrideReason === 'Other'
        ? otherReasonText.trim() || 'Other'
        : data.overrideReason;
    if (effectiveReason.length > 500) return;
    performBulkReassignment({ ...data, overrideReason: effectiveReason });
  };

  const handleReset = () => {
    reset({ assignmentType: 'underwriter', fromUserId: 0, toUserId: 0, overrideReason: '' });
    setOtherReasonText('');
    setSubmitSuccess(null);
  };

  const submitError = usersError
    ? `Failed to load ${assignmentType}s`
    : mutationError
      ? (mutationError as any)?.response?.data?.message ||
        (mutationError as any)?.message ||
        'Failed to perform bulk reassignment'
      : null;

  const getUserDisplayName = (user: User) => user.displayName;

  return (
    <Box>
      <Typography variant="h1" gutterBottom sx={{ mb: 3 }}>
        Bulk User Reassignment
      </Typography>

      <Paper elevation={3} sx={{ p: 4 }}>
        <Box
          component="form"
          onSubmit={handleSubmit(onSubmit)}
          sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}
        >
          {/* Assignment Type Selection */}
          <Controller
            name="assignmentType"
            control={control}
            render={({ field }) => (
              <FormControl fullWidth error={!!errors.assignmentType}>
                <InputLabel>Assignment Type</InputLabel>
                <Select {...field} label="Assignment Type">
                  <MenuItem value="underwriter">
                    Underwriter Assignment
                  </MenuItem>
                  <MenuItem value="casemanager">
                    Case Manager Assignment
                  </MenuItem>
                </Select>
                {errors.assignmentType && (
                  <Typography
                    variant="caption"
                    color="error"
                    sx={{ mt: 0.5, display: 'block' }}
                  >
                    {errors.assignmentType.message}
                  </Typography>
                )}
              </FormControl>
            )}
          />

          {/* From User Selection */}
          <Controller
            name="fromUserId"
            control={control}
            render={({ field: { onChange, value } }) => (
              <Box>
                <Autocomplete
                  options={availableUsers}
                  getOptionLabel={option => getUserDisplayName(option)}
                  value={availableUsers.find(user => user.id === value) || null}
                  onChange={(_, newValue) => {
                    onChange(newValue?.id || 0);
                  }}
                  disabled={loadingUsers}
                  renderInput={params => (
                    <TextField
                      {...params}
                      label="Reassign From User"
                      error={!!errors.fromUserId}
                      helperText={errors.fromUserId?.message}
                    />
                  )}
                  renderOption={(props, option) => (
                    <Box component="li" {...props}>
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 1,
                          width: '100%',
                        }}
                      >
                        <Typography sx={{ flexGrow: 1 }}>
                          {getUserDisplayName(option)}
                        </Typography>
                        {!option.active && (
                          <Chip
                            label="Inactive"
                            size="small"
                            color="warning"
                            variant="outlined"
                          />
                        )}
                        {option.paused && (
                          <Chip
                            label="Paused"
                            size="small"
                            color="info"
                            variant="outlined"
                          />
                        )}
                      </Box>
                    </Box>
                  )}
                  isOptionEqualToValue={(option, selectedValue) =>
                    option.id === selectedValue.id
                  }
                />
              </Box>
            )}
          />

          {/* To User Selection (single-select) */}
          <Controller
            name="toUserId"
            control={control}
            render={({ field: { onChange, value } }) => (
              <Box>
                <Autocomplete
                  options={availableTargetUsers}
                  getOptionLabel={option => getUserDisplayName(option)}
                  value={availableTargetUsers.find(u => u.id === value) || null}
                  onChange={(_, newValue) => {
                    onChange(newValue?.id ?? 0);
                  }}
                  disabled={loadingUsers || !fromUserId}
                  renderInput={params => (
                    <TextField
                      {...params}
                      label="Reassign To User"
                      error={!!errors.toUserId}
                      helperText={errors.toUserId?.message}
                    />
                  )}
                  renderOption={(props, option) => (
                    <Box component="li" {...props}>
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 1,
                          width: '100%',
                        }}
                      >
                        <Typography sx={{ flexGrow: 1 }}>
                          {getUserDisplayName(option)}
                        </Typography>
                        {!option.active && (
                          <Chip
                            label="Inactive"
                            size="small"
                            color="warning"
                            variant="outlined"
                          />
                        )}
                        {option.paused && (
                          <Chip
                            label="Paused"
                            size="small"
                            color="info"
                            variant="outlined"
                          />
                        )}
                      </Box>
                    </Box>
                  )}
                  isOptionEqualToValue={(option, selectedValue) =>
                    option.id === selectedValue.id
                  }
                />
              </Box>
            )}
          />

          {/* Override Reason */}
          <Controller
            name="overrideReason"
            control={control}
            render={({ field }) => (
              <FormControl fullWidth error={!!errors.overrideReason}>
                <InputLabel>Override Reason</InputLabel>
                <Select {...field} label="Override Reason">
                  {OVERRIDE_REASONS.map(reason => (
                    <MenuItem key={reason} value={reason}>
                      {reason}
                    </MenuItem>
                  ))}
                </Select>
                {errors.overrideReason && (
                  <Typography
                    variant="caption"
                    color="error"
                    sx={{ mt: 0.5, display: 'block' }}
                  >
                    {errors.overrideReason.message}
                  </Typography>
                )}
              </FormControl>
            )}
          />

          {isOtherReasonSelected && (
            <TextField
              label="Please specify"
              value={otherReasonText}
              onChange={e => setOtherReasonText(e.target.value)}
              error={otherReasonTooLong}
              helperText={
                otherReasonTooLong
                  ? 'Override reason cannot exceed 500 characters'
                  : undefined
              }
              fullWidth
            />
          )}

          {/* Submit Buttons */}
          <Box sx={{ display: 'flex', gap: 2, pt: 2 }}>
            <Button
              type="submit"
              variant="contained"
              disabled={
                !isValid || otherReasonTooLong || loading || loadingUsers
              }
              startIcon={loading ? <CircularProgress size={20} /> : null}
              size="large"
              sx={{ minWidth: 200 }}
            >
              {loading ? 'Processing...' : 'Perform Bulk Reassignment'}
            </Button>

            <Button
              variant="outlined"
              onClick={handleReset}
              disabled={loading}
              size="large"
            >
              Reset Form
            </Button>
          </Box>
        </Box>

        {/* Status Messages */}
        {(loadingUsers || usersError || submitSuccess || submitError) && (
          <Box sx={{ mt: 3 }}>
            {loadingUsers && (
              <Alert severity="info" icon={<CircularProgress size={20} />}>
                <Typography variant="body2">
                  Loading {assignmentType}s...
                </Typography>
              </Alert>
            )}

            {usersError && (
              <Alert
                severity="warning"
                action={
                  <Button
                    color="inherit"
                    size="small"
                    onClick={() =>
                      queryClient.invalidateQueries({
                        queryKey: ['users', assignmentType],
                      })
                    }
                  >
                    Retry
                  </Button>
                }
              >
                Failed to load {assignmentType}s. Please try again.
              </Alert>
            )}

            {submitSuccess && (
              <Alert severity="success">
                <Typography variant="h6" sx={{ mb: 1 }}>
                  Bulk Reassignment Successful!
                </Typography>
                <Typography variant="body2">
                  Successfully reassigned{' '}
                  <strong>{submitSuccess.updatedCount}</strong> applications
                  {submitSuccess.reassignedTo.length === 0
                    ? ' — unassigned.'
                    : '.'}
                </Typography>
                <Typography variant="body2" sx={{ mt: 0.5 }}>
                  Assignment Type:{' '}
                  <strong>{submitSuccess.assignmentType}</strong>
                </Typography>
              </Alert>
            )}

            {submitError && (
              <Alert severity="error">
                <Typography variant="h6" sx={{ mb: 1 }}>
                  Reassignment Failed
                </Typography>
                <Typography variant="body2">{submitError}</Typography>
              </Alert>
            )}
          </Box>
        )}
      </Paper>
    </Box>
  );
}
